import { createContext, useContext, useMemo } from "react";
import type { PositionNode } from "../api/structure";

/**
 * The user's structure scope (sprint 7 §12, 2026-10-30): with branches chosen on "Pristup korisnika",
 * the structure and sastavnica functions work only on a position inside one of them — the branch's own
 * position included. The first level (no parent) is outside every branch. An empty scope = the whole
 * structure. Reading stays global; the server checks the same rule (403 `OUT_OF_SCOPE`).
 */

export const NO_SCOPE: ReadonlySet<string> = new Set();

/**
 * The signed-in user's branches, provided by `AuthProvider` from `/auth/me`. Outside it (a component
 * rendered on its own) there is no session to narrow, so the default is the whole structure.
 */
export const StructureScopeContext = createContext<ReadonlySet<string>>(NO_SCOPE);

/** `chain` = ids from the top of the structure down to the position itself; `[]` = the first level. */
export function chainInScope(scope: ReadonlySet<string>, chain: readonly string[]): boolean {
  if (scope.size === 0) return true;
  return chain.some((id) => scope.has(id));
}

/**
 * Ids of every loaded node of `forest` inside the scope. `above` = the chain over the forest's roots
 * (a focused branch); empty when the forest starts at the first level.
 */
export function idsInScope(scope: ReadonlySet<string>, forest: readonly PositionNode[], above: readonly string[] = []): Set<string> {
  const out = new Set<string>();
  const walk = (list: readonly PositionNode[], inherited: boolean) => {
    for (const n of list) {
      const inside = inherited || scope.has(n.id);
      if (inside) out.add(n.id);
      if (n.children) walk(n.children, inside);
    }
  };
  walk(forest, chainInScope(scope, above));
  return out;
}

export type StructureScope = {
  /** True when the user has branches — the structure functions do not reach everywhere. */
  limited: boolean;
  inScope: (chain: readonly string[]) => boolean;
};

/** The signed-in user's scope. */
export function useStructureScope(): StructureScope {
  const scope = useStructureScopeIds();
  return useMemo(
    () => ({ limited: scope.size > 0, inScope: (chain: readonly string[]) => chainInScope(scope, chain) }),
    [scope],
  );
}

/** The scope set itself, for walking a whole forest with `idsInScope`. */
export function useStructureScopeIds(): ReadonlySet<string> {
  return useContext(StructureScopeContext);
}
